import { z } from "zod";
import type { LlmClient, LlmRequest, ToolDesign } from "@keystone/core";
import { canonicalSha256 } from "@keystone/core";

/**
 * Agentic attack synthesiser. Given a tool surface plus prior static +
 * agentic findings, asks an LLM to propose 3-5 *novel* input-validation /
 * scope-escape attacks tailored to the spec. Each candidate carries a
 * crisp PROGRAMMATIC confirmation oracle so the in-process runtime decides
 * the verdict — no LLM-as-judge for the confirmation step.
 *
 * Cost model:
 *  - 1 LLM call per round of synthesis
 *  - input tokens: KB summary + tool surface + prior-findings digest ≈ 4–6k
 *  - output tokens: 5 attack candidates ≈ 1–2k
 *  - Sonnet 4.6 round cost: <$0.05; CI reads the committed transcript ($0)
 *
 * Determinism:
 *  - pass RecordedLlmClient for replay; AnthropicLlmClient only when re-recording.
 *  - request envelope is canonicalised + sha-keyed identically to the eval harness.
 */

export const AttackCandidateSchema = z.object({
  id: z.string().min(1),
  /** Tool the attack targets (must be an existing tool name in the design). */
  targetTool: z.string().min(1),
  category: z.enum([
    "path-traversal",
    "type-confusion",
    "oversize-payload",
    "injection-string",
    "scope-escape",
    "annotation-trust",
    "id-enumeration",
    "param-pollution",
    "homoglyph-target"
  ]),
  rationale: z.string(),
  /** Args to pass to the tool — JSON object the runtime would receive. */
  payload: z.record(z.unknown()),
  /**
   * Programmatic confirmation. The oracle is one of a fixed vocabulary the
   * runtime knows how to evaluate — keeps LLM out of the confirmation loop.
   */
  oracle: z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("rejects"), reason: z.string() }),
    z.object({ kind: z.literal("returns-error"), errorContains: z.string().optional() }),
    z.object({ kind: z.literal("response-contains"), needle: z.string() }),
    z.object({ kind: z.literal("response-empty") }),
    z.object({ kind: z.literal("call-throws") })
  ])
});
export type AttackCandidate = z.infer<typeof AttackCandidateSchema>;

export const SynthesisOutputSchema = z.object({
  attacks: z.array(AttackCandidateSchema).max(10)
});

export interface SynthesizeInput {
  design: ToolDesign;
  /** Prior static + agentic findings, summarised; lets the LLM avoid restating known weaknesses. */
  priorFindings?: Array<{ classId?: string; ruleId?: string; toolName: string; rationale: string }>;
  /** IDs of attacks already proposed in earlier rounds (avoid duplicates). */
  excludeIds?: string[];
  llm: LlmClient;
  model?: string;
  /** How many fresh attack candidates to ask for. Default 5; clamp to [3, 10]. */
  count?: number;
}

const SYNTH_INSTRUCTIONS = [
  "You are Keystone's adversarial red-team synthesiser. You see a tool surface and",
  "a list of findings already known. Your job: propose NOVEL attacks the static",
  "scanner and the prior round haven't already caught. Focus on attacks the",
  "in-process tool runtime can confirm WITHOUT calling an LLM — so favour input-",
  "validation (path traversal, oversize, type confusion, parameter pollution,",
  "id enumeration) and scope-escape (calling a destructive tool with read-only",
  "framing). Avoid pure prompt-injection that needs a model in the loop.",
  "",
  "Each attack MUST include:",
  "  id           — short kebab-case identifier (e.g. 'pet-id-traversal')",
  "  targetTool   — exact tool name from the surface you're given",
  "  category     — one of: path-traversal, type-confusion, oversize-payload,",
  "                 injection-string, scope-escape, annotation-trust,",
  "                 id-enumeration, param-pollution, homoglyph-target",
  "  rationale    — one sentence on what the attacker hopes to achieve",
  "  payload      — JSON object with the args you'd pass to the tool",
  "  oracle       — discriminated union, one of:",
  "                 { kind:'rejects', reason:'...' }       — runtime should refuse",
  "                 { kind:'returns-error', errorContains?:'not found' }",
  "                 { kind:'response-contains', needle:'...' }",
  "                 { kind:'response-empty' }",
  "                 { kind:'call-throws' }",
  "",
  "Calibrate severity by picking the right oracle — for safe-by-design APIs the",
  "oracle is typically 'rejects' or 'returns-error', not 'response-contains'.",
  "",
  "STRICT JSON rules (the parser is unforgiving):",
  "  - All values must be literal JSON. Never use JavaScript expressions like",
  "    \"A\".repeat(100000), Array(N).fill(...), Math.pow(2,53), '/*comment*/'.",
  "  - For oversize-payload attacks, use a literal repeated string capped at ~200",
  "    characters (e.g. 200 'A' characters). The runtime fails the same way.",
  "  - For oversize numeric values use 9999999999 (literal), not 10**12.",
  "  - No trailing commas, no comments, no markdown fences.",
  "",
  "Return ONLY JSON: { \"attacks\": [ ...candidates... ] }. No markdown, no prose."
].join("\n");

export function buildSynthesisRequest(input: SynthesizeInput): LlmRequest {
  const model = input.model ?? "claude-sonnet-4-6";
  const count = Math.max(3, Math.min(10, input.count ?? 5));
  const surface = input.design.tools.map((t) => ({
    name: t.name,
    title: t.title,
    description: t.description.slice(0, 240),
    annotations: t.annotations,
    inputProperties: Object.keys((t.inputSchema as { properties?: object } | undefined)?.properties ?? {})
  }));
  const priorBrief = (input.priorFindings ?? []).map((f) =>
    `- [${f.classId ?? f.ruleId ?? "?"}] ${f.toolName}: ${f.rationale.slice(0, 140)}`
  );
  const excluded = input.excludeIds ?? [];
  const userMsg = [
    "Tool surface (JSON):",
    JSON.stringify(surface, null, 2),
    "",
    "Findings already known (no need to restate):",
    priorBrief.length > 0 ? priorBrief.join("\n") : "(none — first round)",
    "",
    `Already-proposed attack ids (do NOT repeat): ${excluded.join(", ") || "(none)"}`,
    "",
    `Propose ${count} novel attacks.`
  ].join("\n");
  return {
    model,
    temperature: 0,
    maxTokens: 3072,
    messages: [
      { role: "system", content: SYNTH_INSTRUCTIONS },
      { role: "user", content: userMsg }
    ]
  };
}

export async function synthesizeAttacks(input: SynthesizeInput): Promise<AttackCandidate[]> {
  const req = buildSynthesisRequest(input);
  const res = await input.llm.complete(req);
  const json = extractJsonObject(res.text);
  let parsed: z.infer<typeof SynthesisOutputSchema>;
  try {
    parsed = SynthesisOutputSchema.parse(JSON.parse(json));
  } catch (e) {
    if (process.env.KEYSTONE_DEBUG_SYNTH === "1") {
      const fs = await import("node:fs");
      fs.writeFileSync("debug-synth-failure.json", res.text);
    }
    throw new Error(
      "agentic-attacks: model output did not match SynthesisOutputSchema. " +
        "Set KEYSTONE_DEBUG_SYNTH=1 to dump full response to debug-synth-failure.json. " +
        "First 200 chars: " +
        res.text.slice(0, 200) +
        " — parse error: " +
        (e as Error).message
    );
  }
  const toolNames = new Set(input.design.tools.map((t) => t.name));
  const excluded = new Set(input.excludeIds ?? []);
  // Sanity filters: drop attacks that target tools we didn't list, or whose id
  // duplicates a prior round.
  return parsed.attacks.filter((a) => toolNames.has(a.targetTool) && !excluded.has(a.id));
}

/** Hash of the canonical synthesis envelope — keys committed transcripts. */
export function synthesisRequestHash(input: SynthesizeInput): string {
  return canonicalSha256(buildSynthesisRequest(input));
}

/**
 * Extract the largest balanced JSON object from a model response. Tolerates
 * markdown code fences, trailing prose, and the occasional unquoted comment.
 */
function extractJsonObject(text: string): string {
  // Strip markdown fences if present.
  let body = text.trim();
  const fence = body.match(/^```(?:json)?\s*([\s\S]*?)```\s*$/);
  if (fence) body = fence[1].trim();
  // Find the first { and walk to the matching } accounting for string escapes.
  const start = body.indexOf("{");
  if (start === -1) return body;
  let depth = 0;
  let inStr = false;
  let esc = false;
  for (let i = start; i < body.length; i += 1) {
    const ch = body[i];
    if (esc) {
      esc = false;
      continue;
    }
    if (ch === "\\" && inStr) {
      esc = true;
      continue;
    }
    if (ch === '"') {
      inStr = !inStr;
      continue;
    }
    if (inStr) continue;
    if (ch === "{") depth += 1;
    else if (ch === "}") {
      depth -= 1;
      if (depth === 0) return body.slice(start, i + 1);
    }
  }
  return body.slice(start);
}
